import React, { useState, useEffect, useRef } from "react"
import firebase from "firebase"
import { X, UploadCloud, Image as ImageIcon, RotateCcw } from "lucide-react"

import { db, storage } from "../../utils/firebase"

import styles from "./portal-upload.module.scss"

const random = require("randomstring")

const ALLOWED_TYPES = ["image/jpeg", "image/jpg", "image/png"]
const MAX_MB = 5

/**
 * Portal-native upload modal for the client portal. Replaces the legacy
 * shared `Window`-based modal so the design matches the rest of the portal.
 *
 * Same upload flow as before: validate type/size, push to Firebase Storage
 * under bookings/{bookingID}/{uploadID}, write a `bookings/{id}/images` doc
 * plus a `logs` entry, then close.
 */
export default function PortalUpload({ bookingID, clientID, close }) {
	const [uploading, setUploading] = useState(false)
	const [progress, setProgress] = useState(0)
	const [error, setError] = useState("")
	const fileInputRef = useRef(null)

	// Close on Escape, prevent body scroll while open.
	useEffect(() => {
		const onKey = (e) => {
			if (e.key === "Escape" && !uploading) close()
		}
		window.addEventListener("keydown", onKey)
		const prevOverflow = document.body.style.overflow
		document.body.style.overflow = "hidden"
		return () => {
			window.removeEventListener("keydown", onKey)
			document.body.style.overflow = prevOverflow
		}
	}, [close, uploading])

	const validate = (file) => {
		if (!ALLOWED_TYPES.includes(file.type)) return "Please choose a JPG or PNG image."
		if (file.size / 1024 / 1024 > MAX_MB) return `Please choose a file under ${MAX_MB} MB.`
		return ""
	}

	const startUpload = (file) => {
		const validationError = validate(file)
		if (validationError) {
			setError(validationError)
			return
		}

		setError("")
		setUploading(true)
		setProgress(0)

		const uploadID = random.generate(20)
		const task = storage.ref(`bookings/${bookingID}/${uploadID}`).put(file, {
			customMetadata: { bookingID, fileID: uploadID, uploadedBy: clientID || "" },
		})

		task.on(
			"state_changed",
			(snapshot) => {
				const pct = Math.round((snapshot.bytesTransferred / snapshot.totalBytes) * 100)
				setProgress(pct)
			},
			(err) => {
				console.log("Upload failed:", err)
				setError(err?.message || "Something went wrong uploading your file. Please try again.")
				setUploading(false)
			},
			async () => {
				try {
					const url = await task.snapshot.ref.getDownloadURL()
					await db.doc(`bookings/${bookingID}/images/${uploadID}`).set(
						{
							created: firebase.firestore.FieldValue.serverTimestamp(),
							public_url: url,
						},
						{ merge: true },
					)
					await db.collection(`bookings/${bookingID}/logs`).add({
						created: firebase.firestore.FieldValue.serverTimestamp(),
						type: "FLIGHT_DETAILS_UPLOADED",
						message: "Flight details were uploaded by the client",
						user: null,
						badge: "INFO",
					})
					close()
				} catch (err) {
					setError(err?.message || "Couldn't finish saving your upload. Please try again.")
					setUploading(false)
				}
			},
		)
	}

	const onFileChange = (e) => {
		const file = e.target.files?.[0]
		if (file) startUpload(file)
		// reset so picking the same file twice still fires onChange
		if (fileInputRef.current) fileInputRef.current.value = ""
	}

	const reset = () => {
		setError("")
		setProgress(0)
	}

	return (
		<div
			className={styles.backdrop}
			onClick={() => !uploading && close()}>
			<div
				className={styles.modal}
				onClick={(e) => e.stopPropagation()}
				role="dialog"
				aria-modal="true">
				<header className={styles.head}>
					<h3>Upload flight details</h3>
					<button
						type="button"
						className={styles.close}
						onClick={close}
						disabled={uploading}
						aria-label="Close">
						<X size={18} />
					</button>
				</header>

				<div className={styles.body}>
					{error ? (
						<div className={styles.errorState}>
							<p className={styles.errorText}>{error}</p>
							<button
								type="button"
								className={styles.retry}
								onClick={reset}>
								<RotateCcw size={15} />
								Try a different file
							</button>
						</div>
					) : uploading ? (
						<div className={styles.uploading}>
							<span className={styles.uploadingIcon}>
								<ImageIcon size={24} />
							</span>
							<p className={styles.uploadingLabel}>Uploading… {progress}%</p>
							<div className={styles.progressBar}>
								<div
									className={styles.progressFill}
									style={{ width: `${progress}%` }}
								/>
							</div>
						</div>
					) : (
						<label className={styles.dropzone}>
							<input
								ref={fileInputRef}
								type="file"
								accept="image/png,image/jpg,image/jpeg"
								onChange={onFileChange}
							/>
							<span className={styles.dropzoneIcon}>
								<UploadCloud size={28} />
							</span>
							<strong>Choose a screenshot</strong>
							<small>JPG or PNG, up to {MAX_MB} MB</small>
						</label>
					)}
				</div>
			</div>
		</div>
	)
}
